/* MECEChoir.jsx -- Philadelphia MECE City Choir page.

   The call-sheet layout: full-bleed photo header, poster type, an ink facts
   strip, then the two things people actually come here to do.

   THE PAGE USED TO BE TWO MAILTO LINKS AND THAT WAS THE WHOLE FUNNEL. Joining
   meant composing a cold email about your own singing voice; booking meant
   guessing what JJ needed to know. Both are forms now, posting to
   /api/intake as type=choir-signup and type=choir-booking. The phone number
   and JJ's address stay in the footer -- the form is the front door, not the
   only one.

   Two forms, two colors, on purpose: singers get terracotta, bookers get
   plum. They are different audiences answering different questions and the
   old page sent both to the same inbox with no way to tell them apart. */

const MECE_VOICE_PARTS = ["Soprano", "Alto", "Tenor", "Bass", "Not sure yet"];
const MECE_EVENT_TYPES = ["Holiday program", "City celebration", "Awards ceremony", "Community event", "Other"];
const MECE_EMPLOYMENT = ["Current employee", "Former employee", "Retired"];
const MECE_EXPERIENCE = ["Never sung in a choir", "Sang in school or church", "Sing regularly now", "Trained vocalist"];
const MECE_BUDGET = [
  "Not sure yet",
  "No budget — City department event",
  "Under $500",
  "$500 – $1,000",
  "$1,000 – $2,500",
  "Over $2,500",
];

function MECEChoirPage() {
  return (
    <div>
      <MeceHero />
      <MeceCallSheet />
      <MeceJoinForm />
      <Marquee items={["Sing with the city", "Every department", "Every neighborhood", "Since 1995"]} />
      <MeceStory />
      <MeceBookingForm />
      <MeceFooterNote />

      <style>{`
        .mece-hero-inner { padding: 96px 0 72px; }
        .mece-parts { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; }
        .mece-row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
        .mece-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        .mece-callsheet { display: grid; grid-template-columns: repeat(4, 1fr); }
        .mece-split { display: grid; grid-template-columns: 1fr 1fr; gap: 46px; align-items: start; }
        .mece-book-split { display: grid; grid-template-columns: 0.85fr 1.15fr; gap: 46px; align-items: start; }
        @media (max-width: 900px) {
          .mece-hero-inner { padding: 64px 0 48px; }
          .mece-parts { grid-template-columns: 1fr 1fr; }
          .mece-row-3, .mece-row-2 { grid-template-columns: 1fr; }
          .mece-callsheet { grid-template-columns: 1fr 1fr; }
          .mece-split, .mece-book-split { grid-template-columns: 1fr; gap: 32px; }
        }
        @media (max-width: 560px) {
          .mece-parts { grid-template-columns: 1fr; }
          .mece-callsheet { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
}

function meceScrollTo(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

/* ---------- hero: photo stretched across the header ---------- */

function MeceHero() {
  return (
    <section style={{ position: "relative", borderBottom: "2px solid var(--c-ink)", overflow: "hidden" }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          backgroundImage: "url(/img/mece-choir.jpg)",
          backgroundSize: "cover", backgroundPosition: "center 40%",
        }}
      />
      {/* Veil is ink, not black: the headline has to clear WCAG over a photo
          without the page turning into a different site's palette. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(195deg, rgba(42,24,32,0.55) 0%, rgba(42,24,32,0.88) 55%, rgba(42,24,32,0.96) 100%)",
        }}
      />
      <div className="container mece-hero-inner" style={{ position: "relative" }}>
        <div className="eyebrow" style={{ color: "var(--c-accent)" }}>
          Municipal Employees Choral Ensemble &middot; Est. 1995
        </div>
        <h1
          style={{
            fontFamily: '"Anton", "Oswald", system-ui, sans-serif',
            fontWeight: 400,
            textTransform: "uppercase",
            fontSize: "clamp(52px, 10vw, 128px)",
            lineHeight: 0.86,
            letterSpacing: "0.005em",
            color: "var(--c-cream)",
            margin: "14px 0 0",
          }}
        >
          MECE is<br /><span style={{ color: "var(--c-accent)" }}>calling.</span>
        </h1>
        <p style={{ maxWidth: 620, marginTop: 20, fontSize: 18, lineHeight: 1.55, color: "#F2E6D6" }}>
          The City&rsquo;s own choir. Current and former City of Philadelphia employees,
          every department, one chorale &mdash; singing at Love Park, the Municipal
          Services Building, DHS, the airport, and wherever Philadelphia gathers.
        </p>
        {/* Buttons, not anchors: the site is hash-routed, so an href="#mece-join"
            rewrites the route and a reload would land on the home page. */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
          <button type="button" className="btn ochre" onClick={() => meceScrollTo("mece-join")}>
            Sing with us &rarr;
          </button>
          <button type="button" className="btn secondary" onClick={() => meceScrollTo("mece-book")}>
            Book the choir &rarr;
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------- call sheet: the four facts people ask for ---------- */

function MeceCallSheet() {
  const cells = [
    { label: "Who sings", value: "City staff, current & former" },
    { label: "To inquire", value: "No audition" },
    { label: "Rehearsals", value: "TBD" },
    { label: "Director", value: "Latief Pinder" },
  ];
  return (
    <section
      className="mece-callsheet"
      style={{ background: "var(--c-ink)", borderBottom: "2px solid var(--c-ink)" }}
    >
      {cells.map((c, i) => (
        <div
          key={c.label}
          style={{
            padding: "16px 32px",
            borderRight: i === cells.length - 1 ? "none" : "1px solid rgba(251,243,226,0.22)",
          }}
        >
          <div className="eyebrow" style={{ color: "var(--c-accent)" }}>{c.label}</div>
          <div
            style={{
              fontFamily: '"Anton", "Oswald", system-ui, sans-serif',
              fontWeight: 400,
              fontSize: 17,
              textTransform: "uppercase",
              color: "var(--c-cream)",
              marginTop: 4,
            }}
          >
            {c.value}
          </div>
        </div>
      ))}
    </section>
  );
}

/* ---------- join ---------- */

function MeceJoinForm() {
  const blank = {
    voicePart: "Not sure yet",
    name: "", email: "", phone: "",
    department: "", employment: MECE_EMPLOYMENT[0], experience: "",
  };
  const [data, setData] = React.useState(blank);
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(null);
  const [error, setError] = React.useState("");
  const update = (k, v) => setData(d => ({ ...d, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const res = await fetch("/api/intake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "choir-signup", ...data }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || `error_${res.status}`);
      setSent({ ref: json.ref });
    } catch (err) {
      setError("Couldn't send that just now. Try again, or email Joseph.J.Pospiech@phila.gov.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="mece-join"
      className="section"
      style={{ background: "var(--c-cream-deep)", borderBottom: "2px solid var(--c-ink)", scrollMarginTop: 80 }}
    >
      <div className="container">
        {sent ? (
          <div style={{ textAlign: "center", padding: "24px 0 8px" }}>
            <div
              style={{
                fontFamily: '"Anton", "Oswald", system-ui, sans-serif',
                fontWeight: 400,
                textTransform: "uppercase",
                fontSize: "clamp(38px, 6vw, 68px)",
                lineHeight: 0.9,
                color: "var(--c-primary)",
              }}
            >
              See you at rehearsal.
            </div>
            <p style={{ maxWidth: 520, margin: "18px auto 0", fontSize: 16, color: "var(--c-ink-soft)", lineHeight: 1.6 }}>
              JJ has your details and will follow up personally with rehearsal information.
              A confirmation is on its way to your inbox. Reference {sent.ref}.
            </p>
            <button
              type="button"
              className="btn secondary"
              style={{ marginTop: 24 }}
              onClick={() => { setSent(null); setData(blank); }}
            >
              Sign up someone else
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="eyebrow">Join the choir</div>
            <h2
              style={{
                fontFamily: '"Anton", "Oswald", system-ui, sans-serif',
                fontWeight: 400,
                textTransform: "uppercase",
                fontSize: "clamp(36px, 5.6vw, 64px)",
                lineHeight: 0.92,
                margin: "10px 0 10px",
              }}
            >
              Which part do <span style={{ color: "var(--c-primary)" }}>you</span> sing?
            </h2>
            <p style={{ fontSize: 16, color: "var(--c-ink-soft)", maxWidth: 620, margin: 0 }}>
              Not sure is a real answer &mdash; most people pick it. The director places you once he
              hears you. Membership is open to current and former City employees.
            </p>

            <div className="mece-parts" style={{ margin: "24px 0 26px" }} role="group" aria-label="Voice part">
              {MECE_VOICE_PARTS.map(part => (
                <MecePartButton
                  key={part}
                  label={part}
                  selected={data.voicePart === part}
                  onSelect={() => update("voicePart", part)}
                />
              ))}
            </div>

            <div className="mece-row-3">
              <MeceField id="mece-name" label="Name" value={data.name} onChange={v => update("name", v)} required />
              <MeceField id="mece-email" label="Email" type="email" value={data.email} onChange={v => update("email", v)} required />
              <MeceField id="mece-phone" label="Phone (optional)" value={data.phone} onChange={v => update("phone", v)} />
            </div>
            <div className="mece-row-3" style={{ marginTop: 14 }}>
              <MeceField id="mece-dept" label="Department or center" value={data.department} onChange={v => update("department", v)} />
              <MeceField
                id="mece-status" label="City employment" as="select"
                value={data.employment} onChange={v => update("employment", v)}
                options={MECE_EMPLOYMENT}
              />
              <MeceField
                id="mece-exp" label="Choir experience (optional)" as="select"
                value={data.experience} onChange={v => update("experience", v)}
                options={["", ...MECE_EXPERIENCE]}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", marginTop: 22 }}>
              <button type="submit" className="btn" disabled={sending}>
                {sending ? "Sending…" : "Put me in →"}
              </button>
              <span style={{ fontSize: 13.5, color: "var(--c-ink-soft)" }}>
                Takes about 20 seconds. JJ follows up personally.
              </span>
            </div>
            {error && (
              <div role="alert" style={{ marginTop: 14, fontSize: 14, color: "var(--c-primary-deep)", fontWeight: 600 }}>
                {error}
              </div>
            )}
          </form>
        )}
      </div>
    </section>
  );
}

function MecePartButton({ label, selected, onSelect }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      style={{
        border: "2px solid var(--c-ink)",
        borderRadius: 12,
        padding: "17px 10px",
        background: selected ? "var(--c-primary)" : "var(--c-paper)",
        color: selected ? "var(--c-paper)" : "var(--c-ink)",
        fontFamily: '"Anton", "Oswald", system-ui, sans-serif',
        fontSize: 20,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        boxShadow: "3px 3px 0 var(--c-ink)",
        transition: "transform 120ms ease, box-shadow 120ms ease",
      }}
    >
      {label}
    </button>
  );
}

/* ---------- story + video ---------- */

function MeceStory() {
  return (
    <section className="section" style={{ borderBottom: "2px solid var(--c-ink)" }}>
      <div className="container mece-split">
        <div>
          <div className="eyebrow">About the choir</div>
          <h2 className="display" style={{ fontSize: "clamp(30px, 4vw, 46px)", margin: "10px 0 16px", lineHeight: 1 }}>
            Voices from every neighborhood.
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.62, color: "var(--c-ink)", margin: "0 0 12px" }}>
            The Municipal Employees Choral Ensemble (M.E.C.E.) is comprised exclusively of
            current and former City of Philadelphia employees from various city departments
            who come together during the Christmas holiday season &mdash; and occasionally
            throughout the year &mdash; to lift their voices in song and spread joy and cheer
            to their co-workers.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.62, color: "var(--c-ink-soft)", margin: "0 0 12px" }}>
            The choir&rsquo;s earliest roots go as far back as the early 80s. The current
            iteration took shape in 1995 as an a capella ensemble. In 1998, Joette Wood-Patrick
            became M.E.C.E.&rsquo;s Director, and shortly thereafter Dwayne Bowens joined as
            Maestro Emeritus of Music. They collaborated on fresh new arrangements, keeping up
            with the times and growing the group into a polished, professional ensemble.
          </p>
          <p style={{ fontSize: 15, lineHeight: 1.62, color: "var(--c-ink-soft)", margin: 0 }}>
            Upon Joette&rsquo;s retirement in 2015, directing was taken over by Toni
            Curtis-Ford. In 2017, Clarence Nelson stepped in as M.E.C.E.&rsquo;s Director, and
            upon Clarence&rsquo;s retirement in 2023, Latief Pinder took on the role and
            continues to direct the choir today.
          </p>
        </div>
        <div>
          <div className="eyebrow">Watch &amp; listen</div>
          <h2 className="display" style={{ fontSize: "clamp(30px, 4vw, 46px)", margin: "10px 0 18px", lineHeight: 1 }}>
            Hear MECE sing.
          </h2>
          <div style={{
            aspectRatio: "16 / 9",
            border: "2px solid var(--c-ink)",
            borderRadius: 14,
            overflow: "hidden",
            background: "var(--c-cream-deep)",
            boxShadow: "var(--shadow-lift)",
          }}>
            <iframe
              src="https://www.youtube-nocookie.com/embed/8K1YoaEZf30?rel=0&modestbranding=1"
              title="Philadelphia MECE City Choir performance"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              loading="lazy"
              style={{ width: "100%", height: "100%", border: 0, display: "block" }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- booking ---------- */

function MeceBookingForm() {
  const blank = {
    contactName: "", organization: "", email: "", phone: "",
    eventDate: "", eventTime: "", eventType: MECE_EVENT_TYPES[0],
    location: "", budget: MECE_BUDGET[0], details: "",
  };
  const [data, setData] = React.useState(blank);
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(null);
  const [error, setError] = React.useState("");
  const update = (k, v) => setData(d => ({ ...d, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const res = await fetch("/api/intake", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "choir-booking", ...data }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || `error_${res.status}`);
      setSent({ ref: json.ref });
    } catch (err) {
      setError("Couldn't send that just now. Try again, or email Joseph.J.Pospiech@phila.gov.");
    } finally {
      setSending(false);
    }
  };

  const venues = [
    ["Municipal Services Building Christmas Celebration", "Yearly"],
    ["Department of Human Services Christmas Celebration", "Yearly"],
    ["Love Park tree lighting", "Holidays"],
    ["City Employees Art Exhibit Awards", "Reception"],
    ["Mural Arts Civic Views", "Installation"],
    ["Philadelphia International Airport", "Holidays"],
  ];

  return (
    <section
      id="mece-book"
      className="section"
      style={{ background: "var(--c-paper)", borderBottom: "2px solid var(--c-ink)", scrollMarginTop: 80 }}
    >
      <div className="container mece-book-split">
        <div>
          <div className="eyebrow">Book the choir</div>
          <h2
            style={{
              fontFamily: '"Anton", "Oswald", system-ui, sans-serif',
              fontWeight: 400,
              textTransform: "uppercase",
              fontSize: "clamp(34px, 4.8vw, 56px)",
              lineHeight: 0.92,
              margin: "10px 0 14px",
            }}
          >
            Bring MECE to <span style={{ color: "var(--c-plum)" }}>your event.</span>
          </h2>
          <p style={{ fontSize: 15.5, color: "var(--c-ink-soft)", lineHeight: 1.6, margin: 0 }}>
            City celebrations, holiday programs, awards ceremonies, community events. Tell us the
            date and what you have in mind &mdash; JJ comes back with availability.
          </p>
          <ul style={{ listStyle: "none", margin: "20px 0 0", padding: 0 }}>
            {venues.map(([name, when]) => (
              <li
                key={name}
                style={{
                  borderTop: "1.5px solid var(--c-ink)",
                  padding: "9px 0",
                  fontSize: 14.5,
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 14,
                }}
              >
                <span>{name}</span>
                <span className="eyebrow" style={{ whiteSpace: "nowrap", paddingTop: 3 }}>{when}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="card" style={{ padding: 0, overflow: "hidden", background: "var(--c-cream)" }}>
          <div style={{ background: "var(--c-plum)", color: "var(--c-cream)", padding: "15px 24px", borderBottom: "2px solid var(--c-ink)" }}>
            <div style={{
              fontFamily: '"Anton", "Oswald", system-ui, sans-serif',
              fontWeight: 400, fontSize: 22, textTransform: "uppercase", letterSpacing: "0.02em",
            }}>
              Request the choir
            </div>
            <div style={{ fontSize: 12.5, opacity: 0.9, marginTop: 2 }}>
              One form, and JJ replies with availability.
            </div>
          </div>

          {sent ? (
            <div style={{ padding: "36px 24px", textAlign: "center" }}>
              <div style={{
                fontFamily: '"Anton", "Oswald", system-ui, sans-serif',
                fontWeight: 400, fontSize: 34, textTransform: "uppercase", color: "var(--c-plum)",
              }}>
                Request received.
              </div>
              <p style={{ maxWidth: 380, margin: "14px auto 0", fontSize: 14.5, color: "var(--c-ink-soft)", lineHeight: 1.6 }}>
                JJ will check the choir&rsquo;s calendar and come back to you with availability and
                details. Reference {sent.ref}.
              </p>
              <button
                type="button"
                className="btn secondary small"
                style={{ marginTop: 20 }}
                onClick={() => { setSent(null); setData(blank); }}
              >
                Send another request
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} style={{ padding: "22px 24px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
              <div className="mece-row-2">
                <MeceField id="mece-b-name" label="Your name" value={data.contactName} onChange={v => update("contactName", v)} required />
                <MeceField id="mece-b-org" label="Organization or department" value={data.organization} onChange={v => update("organization", v)} />
              </div>
              <div className="mece-row-2">
                <MeceField id="mece-b-email" label="Email" type="email" value={data.email} onChange={v => update("email", v)} required />
                <MeceField id="mece-b-phone" label="Phone" value={data.phone} onChange={v => update("phone", v)} />
              </div>
              <div className="mece-row-3">
                <MeceField id="mece-b-date" label="Event date" type="date" value={data.eventDate} onChange={v => update("eventDate", v)} />
                <MeceField id="mece-b-time" label="Start time" type="time" value={data.eventTime} onChange={v => update("eventTime", v)} />
                <MeceField
                  id="mece-b-type" label="Event type" as="select"
                  value={data.eventType} onChange={v => update("eventType", v)}
                  options={MECE_EVENT_TYPES}
                />
              </div>
              <div className="mece-row-2">
                <MeceField id="mece-b-loc" label="Location" value={data.location} onChange={v => update("location", v)} />
                <MeceField
                  id="mece-b-budget" label="Budget" as="select"
                  value={data.budget} onChange={v => update("budget", v)}
                  options={MECE_BUDGET}
                />
              </div>
              <MeceField
                id="mece-b-details" label="What do you have in mind?" as="textarea"
                value={data.details} onChange={v => update("details", v)}
              />
              <button type="submit" className="btn" style={{ background: "var(--c-plum)", color: "var(--c-cream)", width: "100%" }} disabled={sending}>
                {sending ? "Sending…" : "Send booking request →"}
              </button>
              <div style={{ fontSize: 12.5, color: "var(--c-ink-soft)" }}>
                November and December fill up fast &mdash; earlier is better.
              </div>
              {error && (
                <div role="alert" style={{ fontSize: 14, color: "var(--c-primary-deep)", fontWeight: 600 }}>
                  {error}
                </div>
              )}
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------- footer fallback: the form is the front door, not the only one ---------- */

function MeceFooterNote() {
  return (
    <section className="section-tight" style={{ background: "var(--c-ink)", color: "var(--c-cream)" }}>
      <div className="container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
        <div style={{ fontSize: 15 }}>
          Prefer to talk it through? Call the Performing Arts Office at{" "}
          <a href="tel:+12156853585" style={{ color: "var(--c-accent)" }}>215-685-3585</a>{" "}
          or email{" "}
          <a href="mailto:Joseph.J.Pospiech@phila.gov?subject=MECE%20City%20Choir" style={{ color: "var(--c-accent)" }}>
            Joseph.J.Pospiech@phila.gov
          </a>.
        </div>
        <div className="eyebrow" style={{ color: "var(--c-accent)" }}>PPR Performing Arts</div>
      </div>
    </section>
  );
}

/* ---------- one field renderer for both forms ---------- */

function MeceField({ id, label, value, onChange, type = "text", as = "input", options = [], required = false }) {
  const common = {
    id,
    value,
    required,
    onChange: (e) => onChange(e.target.value),
  };
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {as === "select" ? (
        <select {...common}>
          {options.map(o => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
      ) : as === "textarea" ? (
        <textarea {...common} rows={3} />
      ) : (
        <input {...common} type={type} />
      )}
    </div>
  );
}

Object.assign(window, { MECEChoirPage });
